import * as React from "react";
import {
    ConvFluentExpLevel,
    ExperienceLevelType,
    IACL,
    IClinician,
    IExperienceLevel,
    IExperienceLevelOption,
    LowHighExperienceLevel,
    YesNoExperienceLevel,
} from "intake_shared/src/types";
import { useToasts } from "react-toast-notifications";
import store from "../../../../store";
import { Dialog } from "../../bits/Dialog/Dialog";
import { orderBy } from "lodash";
import { FormCheckbox } from "../../bits/FormCheckbox/FormCheckbox";
import { Accordion } from "../../bits/Accordion/Accordion";
import { RadioInput } from "../../bits/RadioInput/RadioInput";
import { DatePicker } from "../../bits/DatePicker/DatePicker";
import moment from "../../../../utils/app_moment";
import styles from "./ExperienceLevels.module.scss";
import { AclContext } from "../../../../contexts/AclContext";
import { canManageClinicianCredentials } from "../../../../appSettings";
import { cn } from "../../../helpers";

interface ExperienceLevelsProps {
    allExperienceLevelOptions: IExperienceLevelOption[];
    clinician: IClinician;
    show: boolean;
    handleClose: () => void;
    onSave?: (experienceLevels: IExperienceLevel) => void;
}

function isLanguageCategory(category: string) {
    return category.toLowerCase().trim() === "language";
}

function isLicensingCategory(category: string) {
    return category.toLowerCase().trim() === "licensing";
}

export function isExperienceLevelRequired(category: string): boolean {
    return !isLanguageCategory(category) && !isLicensingCategory(category);
}

export function canBypassExperienceLevelValidation(
    email: string | undefined,
    authAcl: IACL | undefined,
    userAcl?: IACL
): boolean {
    return canManageClinicianCredentials(
        email,
        Boolean(authAcl?.intake || userAcl?.intake)
    );
}

export function hasOnlyCredentialingChanges(
    allExperienceLevelOptions: IExperienceLevelOption[],
    initialExperienceLevels: IExperienceLevel,
    updatedExperienceLevels: IExperienceLevel
): boolean {
    const optionByValue = new Map(
        allExperienceLevelOptions.map((option) => [option.value, option])
    );
    const experienceLevelKeys = Array.from(
        new Set([
            ...Object.keys(initialExperienceLevels),
            ...Object.keys(updatedExperienceLevels),
        ])
    );
    let hasCredentialingChanges = false;

    for (const experienceLevelKey of experienceLevelKeys) {
        const initialLevel = initialExperienceLevels[experienceLevelKey] ?? {};
        const updatedLevel = updatedExperienceLevels[experienceLevelKey] ?? {};
        const credentialingFields = [
            "level",
            "do_not_want",
            "certified",
            "credential_expiration_date",
        ] as const;
        const changedFields = credentialingFields.filter(
            (field) => initialLevel[field] !== updatedLevel[field]
        );

        if (!changedFields.length) {
            continue;
        }

        const option = optionByValue.get(experienceLevelKey);
        if (!option) {
            return false;
        }

        if (isLicensingCategory(option.category)) {
            hasCredentialingChanges = true;
            continue;
        }

        if (
            option.certification_toogle &&
            changedFields.every(
                (field) =>
                    field === "certified" || field === "credential_expiration_date"
            )
        ) {
            hasCredentialingChanges = true;
            continue;
        }

        return false;
    }

    return hasCredentialingChanges;
}

function getExpirationDate(value?: string): Date | null {
    const date = moment(value, "YYYY-MM-DD", true);
    return date.isValid() ? date.toDate() : null;
}

function updateCredentialAnswer(
    current: IExperienceLevel[string] | undefined,
    changes: IExperienceLevel[string],
    clearExpirationDate: boolean
): IExperienceLevel[string] {
    const updated = { ...current, ...changes };
    if (clearExpirationDate) {
        delete updated.credential_expiration_date;
    }
    return updated;
}

function updateCredentialExpirationDate(
    current: IExperienceLevel[string] | undefined,
    date: Date | null
): IExperienceLevel[string] {
    const updated = { ...current };
    if (date) {
        updated.credential_expiration_date = moment(date).format("YYYY-MM-DD");
    } else {
        delete updated.credential_expiration_date;
    }
    return updated;
}

function getMissingExperienceLevels(
    allExperienceLevelOptions: IExperienceLevelOption[],
    updatedExperienceLevels: IExperienceLevel
): IExperienceLevelOption[] {
    return allExperienceLevelOptions.filter(
        (e) =>
            !updatedExperienceLevels[e.value] &&
            isExperienceLevelRequired(e.category)
    );
}

export default function ExperienceLevels({
    allExperienceLevelOptions,
    clinician,
    show,
    handleClose,
    onSave,
}: ExperienceLevelsProps) {
    const { addToast } = useToasts();
    const aclContext = React.useContext(AclContext);
    const canManageCredentials = canBypassExperienceLevelValidation(
        aclContext?.authUser.user?.email ?? undefined,
        aclContext?.authUser.acl,
        aclContext?.userAcl.acl
    );
    const [updatedExperienceLevels, setUpdatedExperienceLevels] = React.useState(
        () => {
            const initialLevels = clinician.experience_levels || {};
            return initialLevels;
        }
    );

    // adding this so it doesn't stop the clinician for saving changes when there are experience levels not applied to them
    const allExperienceLevelsToCheck = allExperienceLevelOptions.filter(
        (e) =>
            (!e.credential ||
                e.credential === clinician.credential ||
                e.credential === "ALL") &&
            !e.clinician_hidden
    );

    const validateExperienceLevels = () => {
        const missingExperienceLevels = getMissingExperienceLevels(
            allExperienceLevelsToCheck,
            updatedExperienceLevels
        );

        // Check for certifications that are not set
        const missingCertifications = allExperienceLevelsToCheck
            .filter(() => canManageCredentials)
            .filter((e) => isExperienceLevelRequired(e.category))
            .filter((e) => e.certification_toogle)
            .filter((e) => !updatedExperienceLevels[e.value]?.certified)
            .map((e) => e.value);

        // Combine both missing experience levels and missing certifications
        const allMissingLevels = [
            ...missingExperienceLevels.map((e) => e.value),
            ...missingCertifications,
        ];

        if (allMissingLevels.length) {
            const msg = `Missing: ${allMissingLevels.join(", ")}`;
            addToast(`Please update all experience levels. ${msg}`, {
                appearance: "error",
            });
            return false;
        }
        return true;
    };

    const dismissExperienceLevels = () => {
        setUpdatedExperienceLevels(clinician.experience_levels || {});
        handleClose();
    };

    const countProviderStyleYesSelections = (
        experienceLevels: IExperienceLevel
    ): number => {
        return allExperienceLevelOptions
            .filter(
                (e) =>
                    e.category === "Provider Style" &&
                    e.type === ExperienceLevelType.YESNO
            )
            .filter(
                (e) => experienceLevels[e.value]?.level === YesNoExperienceLevel.YES
            ).length;
    };

    React.useEffect(() => {
        if (
            clinician.experience_levels &&
            Object.keys(clinician.experience_levels).length > 0
        ) {
            setUpdatedExperienceLevels(clinician.experience_levels);
        }
    }, [clinician.experience_levels]);

    const groupExperienceLevels = () => {
        const experienceCategories: { [key: string]: JSX.Element[] } = {};
        orderBy(allExperienceLevelOptions, "value", "asc")
            .filter((e) => e.show)
            .filter(
                (e) =>
                    (!e.credential ||
                        e.credential === clinician.credential ||
                        e.credential === "ALL") &&
                    !e.clinician_hidden
            )
            .forEach((experience) => {
                const experienceElement = (
                    <div
                        key={`select-${experience.value}`}
                        className={cn(
                            styles.experienceOption,
                            isLicensingCategory(experience.category) &&
                                styles.licensingOption
                        )}
                    >
                        <span className={styles.experienceValue}>
                            {experience.value}
                            {isExperienceLevelRequired(experience.category) && (
                                <span
                                    className={styles.required}
                                    aria-label="required"
                                >
                                    *
                                </span>
                            )}
                        </span>
                        {Object.values(
                            experience.type === ExperienceLevelType.LOWHIGH
                                ? LowHighExperienceLevel
                                : experience.type === ExperienceLevelType.CONVFLUENT
                                ? ConvFluentExpLevel
                                : YesNoExperienceLevel
                        ).map((level) => {
                            const isProviderStyleYesNo =
                                experience.category === "Provider Style" &&
                                experience.type === ExperienceLevelType.YESNO;
                            const isSelectingYes =
                                level === YesNoExperienceLevel.YES;
                            const currentYesCount = countProviderStyleYesSelections(
                                updatedExperienceLevels
                            );
                            const isCurrentlyYes =
                                updatedExperienceLevels[experience.value]?.level ===
                                YesNoExperienceLevel.YES;

                            return (
                                <RadioInput
                                    disabled={
                                        isLicensingCategory(experience.category) &&
                                        !canManageCredentials
                                    }
                                    checked={
                                        updatedExperienceLevels[experience.value]
                                            ?.level === level
                                    }
                                    label={level}
                                    id={`inline-${experience.value}-${level}`}
                                    key={`inline-${experience.value}-${level}`}
                                    name={`inline-${experience.value}-${level}`}
                                    onChange={() => {
                                        // Check if trying to select "yes" in Provider Style category
                                        if (
                                            isProviderStyleYesNo &&
                                            isSelectingYes &&
                                            !isCurrentlyYes &&
                                            currentYesCount >= 6
                                        ) {
                                            addToast(
                                                "You can only select up to 6 'yes' options in Provider Style",
                                                {
                                                    appearance: "error",
                                                }
                                            );
                                            return;
                                        }

                                        setUpdatedExperienceLevels((current) => ({
                                            ...current,
                                            [experience.value]:
                                                updateCredentialAnswer(
                                                    current[experience.value],
                                                    { level },
                                                    isLicensingCategory(
                                                        experience.category
                                                    ) &&
                                                        level !==
                                                            YesNoExperienceLevel.YES
                                                ),
                                        }));
                                    }}
                                    onClick={() => {
                                        experience.type ===
                                            ExperienceLevelType.CONVFLUENT &&
                                            setUpdatedExperienceLevels({
                                                ...updatedExperienceLevels,
                                                [experience.value]: {},
                                            });
                                    }}
                                />
                            );
                        })}
                        {!isLanguageCategory(experience.category) && (
                            <FormCheckbox
                                disabled={
                                    isLicensingCategory(experience.category) &&
                                    !canManageCredentials
                                }
                                id={`check-lth-${experience.value}`}
                                label="Do not want"
                                noBorder
                                checked={Boolean(
                                    updatedExperienceLevels[experience.value]
                                        ?.do_not_want
                                )}
                                onChange={() => {
                                    setUpdatedExperienceLevels({
                                        ...updatedExperienceLevels,
                                        [experience.value]: {
                                            ...updatedExperienceLevels[
                                                experience.value
                                            ],
                                            do_not_want: !Boolean(
                                                updatedExperienceLevels[
                                                    experience.value
                                                ]?.do_not_want
                                            ),
                                        },
                                    });
                                }}
                            />
                        )}
                        {isLicensingCategory(experience.category) &&
                            updatedExperienceLevels[experience.value]?.level ===
                                YesNoExperienceLevel.YES && (
                                <DatePicker
                                    disabled={!canManageCredentials}
                                    id={`expiration-${experience.value}`}
                                    aria-label={`${experience.value} license expiration date`}
                                    selected={getExpirationDate(
                                        updatedExperienceLevels[experience.value]
                                            ?.credential_expiration_date
                                    )}
                                    onChange={(date) =>
                                        setUpdatedExperienceLevels((current) => ({
                                            ...current,
                                            [experience.value]:
                                                updateCredentialExpirationDate(
                                                    current[experience.value],
                                                    date as Date | null
                                                ),
                                        }))
                                    }
                                    wrapperClass={styles.expirationDate}
                                />
                            )}
                        {experience.certification_toogle && (
                            <>
                                <span className={styles.experienceValue}>
                                    Certified?
                                    {isExperienceLevelRequired(
                                        experience.category
                                    ) &&
                                        canManageCredentials && (
                                            <span
                                                className={styles.required}
                                                aria-label="required"
                                            >
                                                *
                                            </span>
                                        )}
                                </span>
                                {Object.values(YesNoExperienceLevel || {})
                                    .filter(Boolean)
                                    .map((level) => (
                                        <RadioInput
                                            disabled={!canManageCredentials}
                                            checked={
                                                updatedExperienceLevels[
                                                    experience.value
                                                ]?.certified === level
                                            }
                                            onChange={() => {
                                                setUpdatedExperienceLevels(
                                                    (current) => ({
                                                        ...current,
                                                        [experience.value]:
                                                            updateCredentialAnswer(
                                                                current[
                                                                    experience.value
                                                                ],
                                                                {
                                                                    certified: level,
                                                                },
                                                                level !==
                                                                    YesNoExperienceLevel.YES
                                                            ),
                                                    })
                                                );
                                            }}
                                            id={`yesno-${experience.value}-${level}`}
                                            label={level}
                                            type="radio"
                                            key={`yesno-${experience.value}-${level}`}
                                            name={`yesno-${experience.value}`}
                                        />
                                    ))}
                                {updatedExperienceLevels[experience.value]
                                    ?.certified === YesNoExperienceLevel.YES && (
                                    <DatePicker
                                        disabled={!canManageCredentials}
                                        id={`certification-expiration-${experience.value}`}
                                        label="Certification expiration date"
                                        isOptional
                                        selected={getExpirationDate(
                                            updatedExperienceLevels[experience.value]
                                                ?.credential_expiration_date
                                        )}
                                        onChange={(date) =>
                                            setUpdatedExperienceLevels(
                                                (current) => ({
                                                    ...current,
                                                    [experience.value]:
                                                        updateCredentialExpirationDate(
                                                            current[
                                                                experience.value
                                                            ],
                                                            date as Date | null
                                                        ),
                                                })
                                            )
                                        }
                                        wrapperClass={styles.expirationDate}
                                    />
                                )}
                            </>
                        )}
                    </div>
                );
                if (experienceCategories[experience.category]) {
                    experienceCategories[experience.category].push(
                        experienceElement
                    );
                } else {
                    experienceCategories[experience.category] = [experienceElement];
                }
            });
        return experienceCategories;
    };
    const groupedLevels = groupExperienceLevels();

    return (
        <Dialog
            title={`${clinician?.fullname} Experience Levels`}
            open={show}
            onClose={dismissExperienceLevels}
            onSave={async () => {
                const canSaveCredentialingChanges =
                    canManageCredentials &&
                    hasOnlyCredentialingChanges(
                        allExperienceLevelOptions,
                        clinician.experience_levels || {},
                        updatedExperienceLevels
                    );

                if (canSaveCredentialingChanges || validateExperienceLevels()) {
                    try {
                        await store.updateClinicianExperienceLevels(
                            clinician.id || clinician.email,
                            clinician.experience_levels || {},
                            updatedExperienceLevels
                        );
                        onSave?.(updatedExperienceLevels);
                        addToast("Experience levels updated", {
                            appearance: "success",
                        });
                        handleClose();
                    } catch (error) {
                        addToast(
                            "Unable to update experience levels. Please refresh and try again.",
                            { appearance: "error" }
                        );
                    }
                }
            }}
            saveText="Save Changes"
            cancelText="Close"
            onCancel={dismissExperienceLevels}
            wrapperClassName={styles.experienceLevelsDialog}
        >
            <div className={styles.experienceLevels}>
                <div className={styles.requiredLegend}>
                    <span className={styles.required} aria-hidden="true">
                        *
                    </span>
                    Required
                </div>
                {Object.keys(groupedLevels).map((k) => (
                    <Accordion
                        key={`category-${k}`}
                        title={`${k} ${
                            isLanguageCategory(k) || isLicensingCategory(k)
                                ? "(Check all that apply)"
                                : ""
                        }`}
                        variant="bordered"
                        contentClassName={styles.accordionContent}
                    >
                        {isLicensingCategory(k) && (
                            <div className={styles.licensingHeader}>
                                <span>State</span>
                                <span className={styles.licensedHeader}>
                                    Licensed
                                </span>
                                <span>Do not want</span>
                                <span>Expiration date (optional)</span>
                            </div>
                        )}
                        {groupedLevels[k]}
                    </Accordion>
                ))}
            </div>
        </Dialog>
    );
}
